import {
  type FC,
  type KeyboardEvent,
  type RefObject,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { PaletteIcon, X } from "lucide-react";
import ReactDOM from "react-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import CustomColorPicker from "@/components/RightStyle/TailwindStylingPanel/components/input/CustomColorPicker";
import type {
  ComboBoxOptionType,
  DropdownOptionType,
} from "@/components/RightStyle/TailwindStylingPanel/components/input";
import { ComboboxContainer } from "@/components/RightStyle/TailwindStylingPanel/components/input";
import ColorSwatch from "@/components/RightStyle/TailwindStylingPanel/components/input/ColorSwatch";
import type { ColorToken, ColorGroup } from "../types";
import { cn } from "@/lib/utils";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";
import { PropertyType } from "@/tailwind/tailwindUtils";
import {
  cleanBgColorValue,
  COLOUR_FALLBACK,
} from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";
import {
  validateColorNameWithFeedback,
  type ColorValidationResult,
} from "../utils/validation";
import { LightDarkToggle } from "./LightDarkToggle";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { Button } from "@/components/ui/button";
import { usePopoverPosition } from "../hooks/usePopoverPosition";
import { useColorGeneration } from "../hooks/useColorGeneration";
import {
  extractColorValueFromOption,
  buildArbitraryClass,
} from "../utils/colorUtils";
import { useDesignTokensStore } from "../store/designTokensStore";
import { useCanvasId } from "@/utils/utils";

interface ColorPopoverProps {
  isOpen: boolean;
  // Edit mode props
  selectedColor?: ColorToken | undefined;
  selectedColorId?: string | null;
  editingColorInPicker?: string;
  onColorChange?: (colorId: string, newColor: string) => void;
  onSaveNameFromPicker?: (isDark?: boolean) => void;
  // New color mode props
  newColorName?: string;
  newColorValue?: string;
  onCreate?: () => void;
  // Light/Dark mode props
  lightColorToken?: ColorToken | undefined;
  darkColorToken?: ColorToken | undefined;
  // Common props
  onClose: () => void;
  onNameChange: (name: string) => void;
  onValueChange?: (value: string) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  anchorRef?: RefObject<HTMLElement>;
  mousePosition?: { x: number; y: number } | null;
  canEditName?: boolean;
}
interface EditColorPopoverProps {
  isOpen: boolean;
  selectedColor: ColorToken | undefined;
  selectedColorId: string | null;
  editingColorInPicker: string;
  lightColorToken?: ColorToken | undefined;
  darkColorToken?: ColorToken | undefined;
  onColorChange: (colorId: string, newColor: string) => void;
  onSaveNameFromPicker: (isDark?: boolean) => void;
  onClose: () => void;
  onNameChange: (name: string) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  anchorRef?: RefObject<HTMLElement>;
  mousePosition?: { x: number; y: number } | null;
  canEditName?: boolean;
}

export const ColorPopover: FC<ColorPopoverProps> = ({
  isOpen,
  selectedColor,
  selectedColorId,
  editingColorInPicker,
  newColorName,
  newColorValue,
  lightColorToken,
  darkColorToken,
  onClose,
  onNameChange,
  onColorChange,
  onValueChange,
  onSaveNameFromPicker,
  onCreate,
  onKeyDown,
  anchorRef,
  mousePosition,
  canEditName = true,
}) => {
  const existingColorGroups = useDesignTokensStore(
    (state) => state.mergedColorGroups,
  );
  const existingUngroupedColors = useDesignTokensStore(
    (state) => state.mergedColors,
  );

  const canvasId = useCanvasId();
  const hasDarkModeColors = useDarkModeStore((state) => state.isCanvasDarkMode);
  const currentGroup = selectedColor?.group || null;
  // Use the position hook
  const { modalRef, computedTop, computedLeft, isPositioned } =
    usePopoverPosition({
      isOpen,
      anchorRef,
      mousePosition,
    });
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ColorValidationResult | null>(null);
  // Initialize view mode to match canvas dark mode, then allow independent toggling
  const [isViewDarkMode, setIsViewDarkMode] = useState(() => {
    // Use function form to ensure initialization only happens once
    return useDarkModeStore.getState().isCanvasDarkMode;
  });

  // Use the color generation hook
  const {
    generatedLightColor,
    generatedDarkColor,
    hasGeneratedColor,
    generateDarkVariant,
    generateLightVariant,
    resetGeneratedColors,
    setGeneratedColorForMode,
  } = useColorGeneration();

  // Local state for color value to ensure proper synchronization
  const [localColorValue, setLocalColorValue] = useState<string>(() => {
    if (selectedColor) {
      const rawValue = selectedColor.hexColor || selectedColor.color || "";
      return cleanBgColorValue(rawValue);
    }
    return cleanBgColorValue(newColorValue || "");
  });

  // Get the current color value, preferring generated colors
  const getCurrentColorValue = () => {
    if (isViewDarkMode) {
      // In dark mode: prefer generated dark color, fallback to existing dark token, then light token
      if (generatedDarkColor) {
        return generatedDarkColor;
      }
      if (effectiveDarkColorToken) {
        return (
          effectiveDarkColorToken.hexColor || effectiveDarkColorToken.color
        );
      }
    } else {
      // In light mode: prefer generated light color, fallback to existing light token
      if (generatedLightColor) {
        return generatedLightColor;
      }
      if (lightColorToken) {
        return lightColorToken.hexColor || lightColorToken.color;
      }
    }
    return localColorValue;
  };

  // Track if user is actively typing to prevent resetting the input
  const isTypingRef = useRef(false);

  // Determine if this is edit mode or new color mode
  const isEditMode = !!(
    selectedColor &&
    selectedColorId &&
    editingColorInPicker !== undefined
  );

  // Create a function that only updates local state
  const handleSetViewDarkMode = useCallback(
    (isDark: boolean) => {
      setIsViewDarkMode(isDark);

      // Update hasGeneratedColor based on whether we have a generated color for this mode
      const currentGenerated = isDark
        ? generatedDarkColor
        : generatedLightColor;
      // hasGeneratedColor is managed by the hook itself

      // Don't update localColorValue here - let getCurrentColorValue handle the logic
      // This prevents overriding existing token colors when switching modes

      // Force update the color picker when view mode changes
      setShouldForceUpdate(true);
      setTimeout(() => setShouldForceUpdate(false), 100);
    },
    [generatedDarkColor, generatedLightColor],
  );

  // Use the dark token prop directly
  const effectiveDarkColorToken = darkColorToken;
  const isNewMode = !!(
    newColorName !== undefined &&
    newColorValue !== undefined &&
    onCreate
  );

  // Get the current name and value based on mode
  const currentName = isEditMode
    ? editingColorInPicker || ""
    : newColorName || "";

  // Get the current group context for validation
  const effectiveCurrentGroup = isEditMode
    ? selectedColor?.group || null
    : currentGroup;

  // Determine which color token to use based on local light/dark state
  // Always prefer the token for the current mode, but fall back to the other mode if available
  const currentColorToken = useMemo(() => {
    if (isEditMode) {
      if (isViewDarkMode) {
        // In dark mode: prefer dark token, fallback to light token, then selected
        const token =
          effectiveDarkColorToken || lightColorToken || selectedColor;
        return token;
      }
      // In light mode: prefer light token, fallback to dark token, then selected
      const token = lightColorToken || effectiveDarkColorToken || selectedColor;
      return token;
    }
    return selectedColor;
  }, [
    isEditMode,
    lightColorToken,
    effectiveDarkColorToken,
    isViewDarkMode,
    selectedColor,
  ]);

  // Check if we should show the light/dark toggle
  // Always show toggle in edit mode to allow users to switch between light/dark views
  const shouldShowLightDarkToggle = Boolean(isEditMode);

  const inputRef = useRef<HTMLInputElement>(null);

  // Get tailwind color properties for the dropdown
  const backgroundColorProperties = useTailwindProperties(
    PropertyType.BACKGROUND_COLOR,
  );

  useEffect(() => {
    if (!currentName.trim() || !isOpen) {
      setValidationResult(null);
      return;
    }

    // Debounce validation to avoid excessive re-renders
    const timeoutId = setTimeout(() => {
      const result = validateColorNameWithFeedback(
        currentName,
        effectiveCurrentGroup || null,
        existingColorGroups,
        existingUngroupedColors,
        isEditMode,
        selectedColor,
      );
      setValidationResult(result);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    currentName,
    effectiveCurrentGroup,
    existingColorGroups,
    existingUngroupedColors,
    isEditMode,
    selectedColorId,
    isOpen,
  ]);

  useEffect(() => {
    if (!isOpen || (!isEditMode && !isNewMode)) return;
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if currently loading
      if (isLoading) {
        return;
      }

      // Check if click is inside the modal
      if (modalRef.current?.contains(event.target as Node)) {
        return;
      }

      // Check if click is inside any color picker related elements or AlertDialog
      let node = event.target as HTMLElement | null;
      while (node) {
        if (
          node.getAttribute &&
          (node.getAttribute("data-inside-modal") === "true" ||
            node.classList?.contains("color-picker-container") ||
            node.classList?.contains("react-colorful") ||
            node.closest(".color-picker-container") ||
            node.closest(".react-colorful") ||
            // Check for AlertDialog/modal content
            node.getAttribute("role") === "dialog" ||
            node.getAttribute("data-state") === "open" ||
            node.closest("[role='dialog']") ||
            node.closest("[data-state='open']") ||
            // Check for Radix UI portal elements
            node.closest("[data-radix-portal]"))
        ) {
          return;
        }
        node = node.parentElement;
      }

      handlePopoverClose();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);

      // Focus the input after the modal is positioned
      const focusTimeout = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Select all text for easy replacement in edit mode
        }
      }, 100);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        clearTimeout(focusTimeout);
      };
    }
  }, [isOpen, anchorRef?.current]);

  const validateAndExecute = async () => {
    const trimmedName = currentName.trim();
    if (!trimmedName) {
      toast.error("Color name cannot be empty");
      return false;
    }

    // Check validation result
    if (validationResult && !validationResult.valid) {
      toast.error(validationResult.message || "Invalid color name");
      return false;
    }

    setIsLoading(true);
    try {
      // Check if we need to save color changes
      if (
        isEditMode &&
        (generatedLightColor ||
          generatedDarkColor ||
          currentName !== (selectedColor?.name || ""))
      ) {
        // In edit mode with changes, call the parent's save handler
        // which should use updateColor instead of creating new tokens
        if (onSaveNameFromPicker) {
          // Store the color values in the parent component's ref before calling save
          if (onColorChange && selectedColorId) {
            // Update the color value if it was changed
            const colorToSave = isViewDarkMode
              ? generatedDarkColor
              : generatedLightColor;
            if (colorToSave) {
              onColorChange(selectedColorId, colorToSave);
            }
          }

          // Let the parent handle the actual save with the proper updateColor call
          await Promise.resolve(onSaveNameFromPicker(isViewDarkMode));
        }

        // Reset generated colors after saving
        resetGeneratedColors();
      } else if (
        isEditMode &&
        onSaveNameFromPicker &&
        !generatedLightColor &&
        !generatedDarkColor
      ) {
        // Normal edit mode save
        await Promise.resolve(onSaveNameFromPicker(isViewDarkMode));
      } else if (isNewMode && onCreate) {
        // New color mode save
        await Promise.resolve(onCreate());
      }
      return true;
    } catch (error) {
      console.error("Error saving color:", error);
      toast.error("Failed to save color", {
        description: "Please try again",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleContainerKeyDown = (e: KeyboardEvent) => {
    // Only handle Escape at container level - Enter is handled by the input
    if (e.key === "Escape") {
      e.preventDefault();
      if (!isLoading) {
        handlePopoverClose();
      }
    }
    onKeyDown(e);
  };

  // Build preview for color options in dropdown
  const buildBackgroundColorPreview = useCallback(
    (option: ComboBoxOptionType) => {
      // Handle modern CSS syntax with CSS variables
      let backgroundColor = "#000000";

      if (option.css) {
        const cleanedValue = cleanBgColorValue(option.css);
        backgroundColor = cleanedValue;
      }

      return (
        <ColorSwatch
          color={backgroundColor}
          className="w-full h-full"
          size="md"
        />
      );
    },
    [],
  );

  // Set view mode to show existing token when popover opens
  useEffect(() => {
    if (isOpen) {
      // If only one mode has a token, switch to that mode
      if (darkColorToken && !lightColorToken) {
        setIsViewDarkMode(true);
      } else if (lightColorToken && !darkColorToken) {
        setIsViewDarkMode(false);
      }
      // If both exist or neither exist, keep current canvas mode
    }
  }, [isOpen, lightColorToken, darkColorToken]);

  // Track when the current color token changes due to view mode toggle
  const currentColorTokenRef = useRef(currentColorToken);
  const [shouldForceUpdate, setShouldForceUpdate] = useState(false);

  useEffect(() => {
    // Check if currentColorToken changed due to view mode toggle
    const currentValue =
      currentColorToken?.hexColor || currentColorToken?.color;
    const previousValue =
      currentColorTokenRef.current?.hexColor ||
      currentColorTokenRef.current?.color;

    if (currentValue !== previousValue && currentValue && previousValue) {
      setShouldForceUpdate(true);
      // Reset the force update flag after a short delay
      const timeoutId = setTimeout(() => setShouldForceUpdate(false), 100);
      return () => clearTimeout(timeoutId);
    }

    currentColorTokenRef.current = currentColorToken;
  }, [currentColorToken]);

  // Update local color value when selected color changes (but not when user is typing)
  useEffect(() => {
    if (isTypingRef.current) return; // Don't update while user is typing

    if (isEditMode && currentColorToken) {
      const colorValue =
        currentColorToken.hexColor || currentColorToken.color || "";
      if (colorValue) {
        // Use cleanBgColorValue to ensure consistent formatting
        setLocalColorValue(cleanBgColorValue(colorValue));
      }
    } else if (isNewMode && newColorValue !== undefined) {
      setLocalColorValue(cleanBgColorValue(newColorValue));
    }
  }, [currentColorToken, newColorValue, isEditMode, isNewMode]);

  // Create stable handlers using useCallback
  const handleSetValue = useCallback(
    (option: ComboBoxOptionType) => {
      // User finished typing or clicked a suggestion
      isTypingRef.current = false;

      // Extract and clean the color value
      let colorValue = "";

      colorValue = extractColorValueFromOption(
        option,
        backgroundColorProperties,
      );

      // Update local state and generated color state based on current mode
      setLocalColorValue(colorValue);

      if (isEditMode) {
        // Store the color in the appropriate mode's generated state
        setGeneratedColorForMode(colorValue, isViewDarkMode);

        // Don't call parent handlers for edit mode - only save when user clicks Save
        // This prevents overwriting original token data
      } else if (isNewMode && onValueChange) {
        // For new color mode, update the parent's state immediately
        onValueChange(colorValue);
      }
    },
    [isViewDarkMode, backgroundColorProperties, isEditMode, isNewMode],
  );

  const handleInstantUpdateValue = useCallback(
    (option: ComboBoxOptionType) => {
      // User is actively typing
      isTypingRef.current = true;

      // Extract and clean the color value
      let colorValue = "";

      colorValue = extractColorValueFromOption(
        option,
        backgroundColorProperties,
      );

      // Update local state for real-time feedback while typing
      setLocalColorValue(colorValue);

      if (isEditMode) {
        // Store the color in the appropriate mode's generated state while typing
        setGeneratedColorForMode(colorValue, isViewDarkMode);

        // Don't call parent handlers for edit mode typing - only save when user clicks Save
      } else if (isNewMode && onValueChange) {
        // For new color mode, update the parent's state immediately even while typing
        onValueChange(colorValue);
      }
    },
    [isViewDarkMode, backgroundColorProperties, isEditMode, isNewMode],
  );

  const handleDropdownSelected = useCallback(
    (option: DropdownOptionType) => {
      let comboBoxOption = backgroundColorProperties.find(
        (opt) =>
          opt.tailwind === option.value ||
          opt.tailwind === `bg-${option.value}`,
      );

      if (!comboBoxOption) {
        comboBoxOption = {
          tailwind:
            typeof option.value === "string" ? `bg-${option.value}` : "",
          css: "",
          propertyType: PropertyType.BACKGROUND_COLOR,
          ...(option as unknown as { value: string }),
        };
      }

      // Extract color value
      let colorValue = "";
      colorValue = extractColorValueFromOption(
        comboBoxOption,
        backgroundColorProperties,
      );

      // Update local state when dropdown is selected
      setLocalColorValue(colorValue);

      if (isEditMode) {
        // Store the color in the appropriate mode's generated state
        setGeneratedColorForMode(colorValue, isViewDarkMode);

        // Don't call parent handlers for edit mode dropdown changes - only save when user clicks Save
      } else if (isNewMode && onValueChange) {
        // For new color mode, update the parent's state immediately
        onValueChange(colorValue);
      }
    },
    [isViewDarkMode, backgroundColorProperties, isEditMode, isNewMode],
  );

  // Create props that match CustomColorPicker's interface
  const customColorPickerProps = useMemo(() => {
    if (isEditMode && currentColorToken && selectedColorId) {
      return {
        forceUpdate: shouldForceUpdate,
        comboboxOptions: backgroundColorProperties,
        dropdownOptions: [], // We don't use dropdown options in ColorPopover, only combobox
        setValue: handleSetValue,
        instantUpdateValue: handleInstantUpdateValue,
        buildArbitraryClass,
        buildOptionPreview: buildBackgroundColorPreview,
        onDropdownSelected: handleDropdownSelected,
        onClear: () => {
          if (onColorChange) {
            onColorChange(selectedColorId, COLOUR_FALLBACK);
          }
        },
        showHex: true,
        disabled: false,
      };
    }

    if (isNewMode && newColorValue !== undefined) {
      return {
        comboboxOptions: backgroundColorProperties,
        dropdownOptions: [], // We don't use dropdown options in ColorPopover, only combobox
        setValue: handleSetValue,
        instantUpdateValue: handleInstantUpdateValue,
        buildArbitraryClass,
        buildOptionPreview: buildBackgroundColorPreview,
        onDropdownSelected: handleDropdownSelected,
        onClear: () => {
          if (onValueChange) {
            onValueChange(COLOUR_FALLBACK);
          }
        },
        showHex: true,
        disabled: false,
      };
    }

    // Fallback for invalid state
    return {
      comboboxOptions: backgroundColorProperties,
      setValue: () => {},
      instantUpdateValue: () => {},
      buildArbitraryClass,
      buildOptionPreview: buildBackgroundColorPreview,
      onDropdownSelected: () => {},
      onClear: () => {},
      showHex: true,
      disabled: true,
    };
  }, [
    isEditMode,
    isNewMode,
    currentColorToken,
    selectedColorId,
    newColorValue,
    backgroundColorProperties,
    shouldForceUpdate,
  ]);

  const top = computedTop || 48;
  const left = computedLeft || 0;

  // Function to generate dark variant for this specific token (client-side only)
  const handleGenerateIndividualDarkToken = useCallback(() => {
    if (!lightColorToken) {
      console.error(
        "Missing required data for individual dark token generation:",
        { lightColorToken },
      );
      return;
    }

    try {
      generateDarkVariant(lightColorToken);

      // Don't close the popover - let the user review and save
    } catch (error) {
      console.error("❌ Failed to generate individual dark token:", error);
    }
  }, [lightColorToken]);

  // Function to generate light variant for this specific token (client-side only)
  const handleGenerateIndividualLightToken = useCallback(() => {
    if (!effectiveDarkColorToken) {
      console.error(
        "Missing required data for individual light token generation:",
        { darkColorToken: effectiveDarkColorToken },
      );
      return;
    }

    try {
      generateLightVariant(effectiveDarkColorToken);

      // Don't close the popover - let the user review and save
    } catch (error) {
      console.error("Failed to generate individual light token:", error);
    }
  }, [effectiveDarkColorToken]);

  // Function to handle popover close
  const handlePopoverClose = useCallback(() => {
    // For individual missing tokens, we don't auto-generate
    // The user should click Generate and then Save
    // Just close normally
    onClose();
  }, [onClose]);

  const GenerateDarkModeUI = ({
    message,
    buttonText,
    onButtonClick,
  }: {
    message: string;
    buttonText: string;
    onButtonClick: () => void | Promise<void>;
  }) => (
    <div className="flex flex-col items-center justify-center py-4 ">
      <div className="my-8 text-center flex flex-col items-center justify-center space-y-2 gap-2">
        <PaletteIcon className="w-4 h-4 text-text-primary" />
        <div className="text-2xs text-text-secondary mb-1">{message}</div>
        <Button
          type="button"
          onClick={onButtonClick}
          className="h-7 px-4 py-2 bg-primary-0 text-text-primary text-xs rounded-md hover:bg-primary-100 transition-colors"
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );

  // Hide the modal until position is calculated to prevent flicker
  if (!isOpen || (!isEditMode && !isNewMode)) {
    return null;
  }

  const missingIndividualDarkToken =
    isEditMode &&
    isViewDarkMode &&
    !effectiveDarkColorToken &&
    lightColorToken &&
    !hasGeneratedColor &&
    !generatedDarkColor;

  const missingLightToken =
    isEditMode &&
    !isViewDarkMode &&
    !lightColorToken &&
    effectiveDarkColorToken &&
    !hasGeneratedColor &&
    !generatedLightColor;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop for click outside detection */}
      <div
        className="fixed inset-0 z-99999"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isLoading) {
            handlePopoverClose();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !isLoading) {
            handlePopoverClose();
          }
        }}
        tabIndex={-1}
        role="button"
        aria-label="Close modal"
      />
      <dialog
        ref={modalRef}
        // why does this have auto margin? m-0 to fix the size calculation issue for anchoring
        className={cn(
          "fixed z-100000 w-64 bg-secondary-0 rounded-md border-secondary-500 border text-xs m-0",
          "bg-[#131519] border-[#2f3035]",
          !isPositioned && "opacity-0 pointer-events-none", // Hide until positioned
        )}
        style={{
          top: `${top}px`,
          left: `${left}px`,
        }}
        onKeyDown={handleContainerKeyDown}
        open={isOpen}
        data-inside-modal="true"
        aria-labelledby="color-popover-title"
        aria-describedby="color-popover-description"
        onClick={(e) => {
          // Prevent clicks inside the modal from bubbling to backdrop
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="flex items-center p-4 pb-2 pl-[8px] pr-[8px] pt-[8px] justify-between">
          <h3 id="color-popover-title" className="text-text-primary text-xs">
            {isEditMode ? "Edit color style" : "New color style"}
          </h3>
          <button
            type="button"
            onClick={handlePopoverClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Header Divider */}
        <div className="border-b border-[#2f3035]" />

        {/* Body Content */}
        <div className="p-2 space-y-2 items-center justify-center">
          {/* Color Name Input */}
          <div className="space-y-1">
            <Input
              ref={inputRef}
              type="text"
              value={currentName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="color-name or group/color-name"
              className="bg-[#1f1f22] border border-transparent text-xs text-[#f5f5f6] placeholder:text-[#808086] focus:outline-hidden focus:border-[#6448ef] rounded-md h-[28px] px-[8px] py-[2px] w-full"
              aria-label="Color name"
              aria-describedby="color-popover-description"
              onKeyDown={async (e) => {
                // Handle Enter and Escape at the input level for better UX
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!isLoading && (await validateAndExecute())) {
                    onClose();
                  }
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  if (!isLoading) {
                    handlePopoverClose();
                  }
                }
              }}
              disabled={isLoading || !canEditName}
            />
            {validationResult && currentName.trim() && !isLoading && (
              <div
                className={cn(
                  "text-xs px-1",
                  validationResult.type === "error" && "text-text-error",
                  validationResult.type === "warning" && "text-alert-50",
                  validationResult.type === "success" && "text-[#808086]",
                )}
              >
                {validationResult.message}
              </div>
            )}
          </div>

          {/* Light/Dark Mode Toggle - show based on our logic */}
          {shouldShowLightDarkToggle && (
            <div className="flex items-center justify-between w-full">
              <LightDarkToggle
                hasDarkModeColors={hasDarkModeColors}
                canvasId={canvasId || ""}
                variant="full-width"
                isViewDarkMode={isViewDarkMode}
                setViewDarkMode={handleSetViewDarkMode}
              />
            </div>
          )}

          {/* Individual token generation UI */}
          {missingIndividualDarkToken ? (
            /* Individual token missing dark variant - show generate UI for this specific token */
            <GenerateDarkModeUI
              message={
                "This token doesn't have a dark mode version yet. Create one to get started."
              }
              buttonText="Generate"
              onButtonClick={handleGenerateIndividualDarkToken}
            />
          ) : missingLightToken ? (
            /* Individual token missing light variant - show generate UI for this specific token */
            <GenerateDarkModeUI
              message={
                "This token doesn't have a light mode version yet. Create one to get started."
              }
              buttonText="Generate"
              onButtonClick={handleGenerateIndividualLightToken}
            />
          ) : (
            /* Color Picker using CustomColorPicker - show when tokens exist */
            <ComboboxContainer>
              <CustomColorPicker
                {...customColorPickerProps}
                propertyState={{
                  propertyValue: getCurrentColorValue(),
                  displayValue: getCurrentColorValue(),
                  isSet: true,
                  cssValue: getCurrentColorValue(),
                }}
                tooltipContent={null}
              />
            </ComboboxContainer>
          )}

          {/* Action Button */}
          {((!missingIndividualDarkToken && !missingLightToken) ||
            hasGeneratedColor) && (
            <div className="flex w-full">
              <Button
                type="button"
                onClick={async () => {
                  if (!isLoading && (await validateAndExecute())) {
                    onClose();
                  }
                }}
                className="h-7 px-3 py-1.5 bg-primary-0 text-text-primary text-xs rounded-md hover:bg-primary-100 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={
                  !currentName.trim() ||
                  isLoading ||
                  validationResult?.valid === false
                }
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full animate-spin" />
                    Saving...
                  </div>
                ) : isEditMode ? (
                  "Save"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          )}
        </div>
      </dialog>
    </>,
    document.getElementById("styling-panel-portal-root") || document.body,
  );
};

// editing a color
export const EditColorPopover: FC<EditColorPopoverProps> = (props) => {
  return <ColorPopover {...props} />;
};

// creating a color
interface NewColorPopoverProps {
  isOpen: boolean;
  newColorName: string;
  newColorValue: string;
  onClose: () => void;
  onNameChange: (name: string) => void;
  onValueChange: (value: string) => void;
  onCreate: () => void;
  onKeyDown: (e: KeyboardEvent) => void;
  anchorRef?: RefObject<HTMLElement>;
}

// Legacy wrapper for NewColorPopover
export const NewColorPopover: FC<NewColorPopoverProps> = (props) => {
  return <ColorPopover {...props} />;
};
